import { filterClasses } from "@swivl-mono/helpers";
import {
  BOT_SEQUENCE_CANCEL_REASONS,
  BOT_SEQUENCE_FAILURE_REASON,
  BOT_SEQUENCE_SKIP_REASONS,
  BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE,
  BOT_SEQUENCE_UNKNOWN_REASON,
  BotSequenceSubjectExternalIds,
  BotSequenceSuppressedOutcome,
  BotSequenceType
} from "@swivl-mono/types";
import { DropdownOptionItem, DropdownOptionVariant, SelectItem } from "@swivl-mono/ui";
import { DateTime } from "luxon";

/** The segmented control's order — PAST_DUE is the page default. */
export const SEQUENCE_ORDER: BotSequenceType[] = [
  BotSequenceType.PAST_DUE,
  BotSequenceType.ABANDONED_CART,
  BotSequenceType.MOVE_IN,
  BotSequenceType.MOVE_OUT
];

export const SEQUENCE_LABELS: Record<BotSequenceType, string> = {
  [BotSequenceType.PAST_DUE]: "Past due",
  [BotSequenceType.ABANDONED_CART]: "Abandoned cart",
  [BotSequenceType.MOVE_IN]: "Move in",
  [BotSequenceType.MOVE_OUT]: "Move out"
};

/** Grey = expected behavior, orange = blocked at fire time, red = an error. */
export const OUTCOME_CHIP_CLASSES: Record<BotSequenceSuppressedOutcome, string> = {
  [BotSequenceSuppressedOutcome.SKIPPED]: "bg-grey-100 text-grey-700",
  [BotSequenceSuppressedOutcome.CANCELLED]: "bg-orange-50 text-orange-700",
  [BotSequenceSuppressedOutcome.FAILED]: "bg-red-50 text-red-900"
};

export const OUTCOME_LABELS: Record<BotSequenceSuppressedOutcome, string> = {
  [BotSequenceSuppressedOutcome.SKIPPED]: "Skipped",
  [BotSequenceSuppressedOutcome.CANCELLED]: "Cancelled",
  [BotSequenceSuppressedOutcome.FAILED]: "Failed"
};

/**
 * The reason select is family-qualified: `suppressed` exists in both the skip and cancel
 * families and means different things, so the value carries the outcome with it.
 */
export const REASON_FILTER_SEPARATOR = "::";

export function encodeReasonFilter(outcome: BotSequenceSuppressedOutcome, reason: string): string {
  return `${outcome}${REASON_FILTER_SEPARATOR}${reason}`;
}

export function decodeReasonFilter(value: string): { outcome: BotSequenceSuppressedOutcome; reason: string } | null {
  const [outcome, reason] = value.split(REASON_FILTER_SEPARATOR);
  if (!outcome || !reason) {
    return null;
  }
  return { outcome: outcome as BotSequenceSuppressedOutcome, reason };
}

/**
 * The reason list is a convenience, not a validation gate — a reason that appears in data
 * before this list is updated still renders in the table with its raw string.
 */
const REASONS_BY_OUTCOME: { outcome: BotSequenceSuppressedOutcome; reasons: readonly string[] }[] = [
  { outcome: BotSequenceSuppressedOutcome.SKIPPED, reasons: BOT_SEQUENCE_SKIP_REASONS },
  { outcome: BotSequenceSuppressedOutcome.CANCELLED, reasons: [...BOT_SEQUENCE_CANCEL_REASONS, BOT_SEQUENCE_UNKNOWN_REASON] },
  { outcome: BotSequenceSuppressedOutcome.FAILED, reasons: [BOT_SEQUENCE_FAILURE_REASON] }
];

export const REASON_FILTER_OPTIONS: SelectItem[] = REASONS_BY_OUTCOME.map(({ outcome, reasons }) => ({
  type: "group",
  label: OUTCOME_LABELS[outcome],
  options: reasons.map((reason) => ({ value: encodeReasonFilter(outcome, reason), label: reason }))
}));

/** The same taxonomy as FilterDropdown groups — one single-select group per outcome family. */
export const REASON_DROPDOWN_GROUPS: DropdownOptionItem[] = REASONS_BY_OUTCOME.map(({ outcome, reasons }) => ({
  label: OUTCOME_LABELS[outcome],
  value: outcome,
  isMultiSelect: false,
  options: reasons.map((reason) => ({
    value: encodeReasonFilter(outcome, reason),
    label: reason,
    variant: DropdownOptionVariant.WITH_CHECKBOX
  }))
}));

/**
 * An instant in the page's fixed zone, always with the abbreviation visible (`EST`/`EDT`)
 * so the number is never ambiguous — e.g. `Aug 18, 14:03 EDT`.
 */
export function formatInstant(iso: string | null | undefined): string {
  if (!iso) {
    return "—";
  }
  const time = DateTime.fromISO(iso).setZone(BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE);
  if (!time.isValid) {
    return "—";
  }
  return `${time.toFormat("LLL d, HH:mm")} ${time.offsetNameShort}`;
}

/** Calendar days between now and the instant, counted in the page's fixed zone. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) {
    return null;
  }
  const target = DateTime.fromISO(iso).setZone(BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE);
  if (!target.isValid) {
    return null;
  }
  const today = DateTime.now().setZone(BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE).startOf("day");
  return Math.round(target.startOf("day").diff(today, "days").days);
}

export function formatExpiresIn(iso: string | null | undefined): string {
  const days = daysUntil(iso);
  if (days === null) {
    return "";
  }
  const zone = DateTime.now().setZone(BOT_SEQUENCE_SUPPRESSED_EVENTS_DISPLAY_TIMEZONE).offsetNameShort;
  if (days <= 0) {
    return `expires today (${zone})`;
  }
  return `expires in ${days} d (${zone})`;
}

/** The sequence's subject, named by the type: a lease for all but the cart. */
export function formatSubject(leaseId: number | null, leadId: number | null): string {
  if (leaseId !== null) {
    return `Lease #${leaseId}`;
  }
  if (leadId !== null) {
    return `Lead #${leadId}`;
  }
  return "—";
}

/** A user with a broken/missing join still identifies as their internal id. */
export function formatUser(userName: string | null, userId: number): string {
  return userName || `#${userId}`;
}

export function formatOrgLocation(orgName: string | null, orgId: number, locationName: string | null): string {
  const org = orgName || `Org #${orgId}`;
  return locationName ? `${org} · ${locationName}` : `${org} · —`;
}

interface IdListProps {
  ids: { label: string; value: string | number | null }[];
}

/** A compact `label #value` run used by both detail panels. Nulls render as `—`. */
export function IdList({ ids }: IdListProps): React.ReactElement {
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
      {ids.map((id) => (
        <span key={id.label} className="text-grey-700">
          {id.label} <span className="font-mono font-medium">{id.value === null || id.value === "" ? "—" : id.value}</span>
        </span>
      ))}
    </div>
  );
}

export function externalIdList(externalIds: BotSequenceSubjectExternalIds): { label: string; value: string | null }[] {
  return [
    { label: "FMS lease", value: externalIds.leaseId },
    { label: "FMS lead", value: externalIds.leadId },
    { label: "FMS tenant", value: externalIds.tenantId },
    { label: "FMS unit", value: externalIds.unitId },
    { label: "FMS location", value: externalIds.locationId }
  ];
}

interface OutcomeChipProps {
  outcome: BotSequenceSuppressedOutcome;
}

export function OutcomeChip({ outcome }: OutcomeChipProps): React.ReactElement {
  return (
    <span className={filterClasses("rounded-full px-2 py-0.5 text-xs font-medium", OUTCOME_CHIP_CLASSES[outcome])}>{OUTCOME_LABELS[outcome]}</span>
  );
}

/**
 * api-helper wraps non-OK responses as `HTTP error <status>: <body>` — unwrap the body.
 * The server's errorHandler puts the real reason of a thrown Error in `error` (with a
 * generic "Internal Server Error" in `message`), so prefer `error` over `message`.
 */
export function extractErrorReason(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const wrapped = message.match(/^HTTP error \d+: (.*)$/s);
  if (!wrapped) {
    return message;
  }
  try {
    const body = JSON.parse(wrapped[1]);
    const reason = body?.error ?? body?.message;
    return reason ? String(reason) : wrapped[1];
  } catch {
    return wrapped[1];
  }
}

/**
 * Display depth of the page (the Contact Center v3 agent/admin pattern): agents see the
 * human-readable tables and calculations; the admin level additionally reveals internal /
 * FMS ids and the engine's recorded actions (timeline, raw status, re-dispatches). The
 * ceiling comes from the ACCOUNT KIND — only Swivl staff may switch to admin.
 */
export type SuppressionsViewLevel = "agent" | "admin";

const VIEW_LEVEL_STORAGE_KEY = "sequenceSuppressions.viewLevel";

export function loadViewLevel(isStaff: boolean): SuppressionsViewLevel {
  if (!isStaff) {
    return "agent";
  }
  try {
    return localStorage.getItem(VIEW_LEVEL_STORAGE_KEY) === "admin" ? "admin" : "agent";
  } catch {
    return "agent";
  }
}

export function storeViewLevel(level: SuppressionsViewLevel): void {
  try {
    localStorage.setItem(VIEW_LEVEL_STORAGE_KEY, level);
  } catch {
    // Persistence is a convenience — a blocked localStorage never breaks the toggle.
  }
}

/** "Showing X–Y of Z" — the same pager copy on both tabs. */
export function pagerLabel(page: number, pageSize: number, total: number): string {
  if (total === 0) {
    return "Showing 0 of 0";
  }
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return `Showing ${first}–${last} of ${total}`;
}
